import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAllColors } from "../../../data/getters";
import colorApi from "../../../data/api/colorApi";
import { useLanguage } from "../../helpers/LanguageContext";
import {
  cropBase64Center,
  fileToBase64,
  sortColors,
  sortColorsById
} from "./colorsPageUtils";

const COLUMNS = [
  ["colorId", "id"],
  [null, "image"],
  ["name", "name"],
  ["dimension", "dimension"],
  ["materialType", "material"],
  ["active", "available"],
  ["painted", "painted"],
  ["rotable", "rotatable"],
  ["splitDimension", "splittable"],
  ["splitDimension", "splitDimension"],
  ["groupName", "group"],
  ["price", "price"],
  [null, "millings"]
];

export default function useColorsPageController() {
  const dispatch = useDispatch();
  const { t } = useLanguage();
  const role = useSelector((state) => state.auth.user?.role);
  const colorsFromStore = useSelector((state) => state.colors || []);
  const millings = useSelector((state) => state.millings || []);
  const [colors, setColors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: null, dir: "asc" });
  const [showColorModal, setShowColorModal] = useState(false);
  const [editingColor, setEditingColor] = useState(null);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [insertLoading, setInsertLoading] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState("");
  const tableContainerRef = useRef(null);
  const canModify = ["admin", "companyUser", "companyAdmin"].includes(role);

  const loadColors = useCallback(async () => {
    setLoading(true);
    try {
      const storedColors = await dispatch(getAllColors());
      setColors(storedColors || []);
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    loadColors();
  }, [loadColors]);
  useEffect(() => {
    if (colorsFromStore.length) setColors(colorsFromStore);
  }, [colorsFromStore]);

  const sortedColors = useMemo(
    () => sortColors(sortColorsById(colors), sortConfig),
    [colors, sortConfig]
  );
  const handleSort = useCallback(
    (key) =>
      setSortConfig((previous) => ({
        key,
        dir: previous.key === key && previous.dir === "asc" ? "desc" : "asc"
      })),
    []
  );
  const sortArrow = useCallback(
    (key) =>
      sortConfig.key !== key ? " ↕" : sortConfig.dir === "asc" ? " ↑" : " ↓",
    [sortConfig]
  );
  const handleRowClick = useCallback(
    (color) => {
      if (canModify) {
        setEditingColor(color);
        setShowColorModal(true);
      }
    },
    [canModify]
  );
  const closeColorModal = useCallback(async () => {
    setShowColorModal(false);
    setEditingColor(null);
    await loadColors();
  }, [loadColors]);

  const getMillingNames = useCallback(
    (ids) => {
      const millingIds =
        typeof ids === "string"
          ? ids.split(",").map(Number)
          : Array.isArray(ids)
            ? ids
            : [];
      return millingIds
        .map((id) => millings.find((milling) => milling.millingId === id)?.name)
        .filter(Boolean);
    },
    [millings]
  );

  const handleBulkInsert = useCallback(
    async (colorDataArray) => {
      setInsertLoading(true);
      let successCount = 0;
      let failCount = 0;
      for (const color of colorDataArray) {
        try {
          const previewImage = await fileToBase64(color.imageFile);
          const reduced = await cropBase64Center(previewImage);
          await dispatch(
            colorApi.createColorApi(
              {
                imageId: 0,
                active: color.active,
                dimension: color.dimension || "[2780,2050,18]",
                materialType: color.materialType,
                name: color.name,
                rotable: color.rotable,
                price: color.price,
                groupName: color.groupName,
                imageContentType: previewImage.split(";")[0].split(":")[1],
                imageData: previewImage.split(",")[1],
                imageDataReduced: reduced.split(",")[1],
                splitDimension: color.splitDimension || null,
                millingAssignments: ""
              },
              null
            )
          );
          successCount++;
        } catch (error) {
          failCount++;
          console.error(`Failed to insert color: ${color.name}`, error);
        }
      }
      setShowBulkModal(false);
      await loadColors();
      setInsertLoading(false);
      window.alert(
        `${successCount} szín feltöltve sikeresen${failCount > 0 ? `, ${failCount} hiba` : ""}`
      );
    },
    [dispatch, loadColors]
  );

  return {
    t,
    columns: COLUMNS,
    colors: sortedColors,
    loading,
    canModify,
    insertLoading,
    sortArrow,
    handleSort,
    handleRowClick,
    getMillingNames,
    tableContainerRef,
    showColorModal,
    editingColor,
    closeColorModal,
    openColorModal: () => {
      setEditingColor(null);
      setShowColorModal(true);
    },
    showBulkModal,
    setShowBulkModal,
    handleBulkInsert,
    fullscreenImage,
    setFullscreenImage
  };
}
